import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  Box,
  Button,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VoiceWaveform from "./VoiceWaveform";
import { StyledButton } from "../StyledComponents";
import { CountdownCircleTimer } from "react-countdown-circle-timer";

const VoiceInputModal = ({
  openMicModal,
  handleCloseMicModal,
  language,
  setLanguage,
  sectionToEdit,
  setSectionToEdit,
  isRecording,
  handleStartRecording,
  handleStopRecording,
  streamRef,
  audioBlob,
  handleSubmitRecording,
  handleSubmitRecordingEdit,
  handlePauseRecording,
  handleResumeRecording,
  isPaused,
  voiceTranscripton,
  limitMessage,
}) => {
  const [mode, setMode] = useState("add");
  const [voiceText, setVoiceText] = useState("");
  const [timerKey, setTimerKey] = useState(0); // used to reset the timer
  const prevRecordingRef = useRef(isRecording);

  useEffect(() => {
    if (voiceTranscripton) {
      setVoiceText((prev) => prev + voiceTranscripton + "\n");
    }
  }, [voiceTranscripton]);

  // Detect transitions of isRecording to reset/start/stop timer
  useEffect(() => {
    // start -> increment key to ensure fresh timer instance
    if (isRecording && !prevRecordingRef.current) {
      setTimerKey((k) => k + 1);
    }
    // stop -> increment key to reset (and the timer will be hidden because isRecording=false)
    if (!isRecording && prevRecordingRef.current) {
      setTimerKey((k) => k + 1);
    }
    prevRecordingRef.current = isRecording;
  }, [isRecording]);

  const languages = [
    { code: "en", name: "English" },
    { code: "hi", name: "Hindi" },
    { code: "bn", name: "Bengali" },
    { code: "as", name: "Assamese" },
    { code: "gu", name: "Gujarati" },
    { code: "kn", name: "Kannada" },
    { code: "ml", name: "Malayalam" },
    { code: "pa", name: "Punjabi" },
    { code: "ta", name: "Tamil" },
    { code: "te", name: "Telugu" },
    { code: "od", name: "Odia" },
  ];

  // Helper to format remaining seconds as MM:SS
  const formatMMSS = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  return (
    <Dialog
      open={openMicModal}
      onClose={handleCloseMicModal}
      fullWidth
      maxWidth="sm"
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: "bold",
          fontSize: "1.2rem",
          color: "#125386",
        }}
      >
        Voice Input Settings
        <IconButton onClick={handleCloseMicModal}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Mode Tabs */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          marginLeft: 1,
          marginRight: 1,
        }}
      >
        <Tabs
          value={mode}
          onChange={(e, newValue) => {
            if (isRecording) return; // Block tab changes if recording is on
            setMode(newValue);
          }}
          variant="fullWidth"
          textColor="inherit"
          TabIndicatorProps={{
            style: { display: "none", boxShadow: "0 4px 4px 0 #C7C7CC inset" },
          }}
        >
          <Tab
            label="ADD"
            value="add"
            sx={{
              fontWeight: "bold",
              bgcolor: mode === "add" ? "#f5f5f5" : "transparent",
              boxShadow: "0 4px 4px 0 #C7C7CC inset",
            }}
          />
          <Tab
            label="EDIT"
            value="edit"
            sx={{
              fontWeight: "bold",
              bgcolor: mode === "edit" ? "#f5f5f5" : "transparent",
              boxShadow: "0 4px 4px 0 #C7C7CC inset",
            }}
          />
          <Tab
            label="TRANSCRIPT"
            value="transcript"
            sx={{
              fontWeight: "bold",
              bgcolor: mode === "transcript" ? "#f5f5f5" : "transparent",
              boxShadow: "0 4px 4px 0 #C7C7CC inset",
            }}
          />
        </Tabs>
      </Box>

      {/* Content */}
      {mode === "transcript" ? (
        <DialogContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Transcription:
          </Typography>

          <Box
            sx={{
              p: 2,
              minHeight: "80px",
              maxHeight: "200px",
              overflowY: "auto",
              backgroundColor: "#f7f7f7",
              borderRadius: 2,
              border: "1px solid #e0e0e0",
              boxShadow: "inset 0 1px 3px rgba(0,0,0,0.08)",
            }}
          >
            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
              {voiceText || "No transcription available."}
            </Typography>
          </Box>
        </DialogContent>
      ) : (
        <DialogContent>
          {/* Language Dropdown */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              disabled={isRecording}
              displayEmpty
              MenuProps={{
                disablePortal: true,
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                PaperProps: { style: { maxHeight: 200, width: "auto" } },
              }}
            >
              {languages.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Section Dropdown for Edit Mode */}
          {mode === "edit" && (
            <FormControl fullWidth sx={{ mb: 2 }} variant="outlined">
              <Select
                multiple
                value={sectionToEdit}
                onChange={(e) => setSectionToEdit(e.target.value)}
                disabled={isRecording}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected || selected.length === 0) {
                    return <>Select one or more sections</>;
                  }
                  return selected.join(", ");
                }}
                MenuProps={{
                  disablePortal: true,
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  transformOrigin: { vertical: "top", horizontal: "left" },
                  PaperProps: { style: { maxHeight: 180, width: "auto" } },
                }}
              >
                <MenuItem value="Chief Complaints">Chief Complaints</MenuItem>
                <MenuItem value="Tests/Investigations">
                  Tests/Investigations
                </MenuItem>
                <MenuItem value="Diagnosis">Diagnosis</MenuItem>
                <MenuItem value="Procedures">Procedures</MenuItem>
                <MenuItem value="Medications">Medications</MenuItem>
                <MenuItem value="FollowUp">Follow Up</MenuItem>
                <MenuItem value="Referral">Referral</MenuItem>
                <MenuItem value="VitalSigns">Examinations</MenuItem>
                <MenuItem value="Medical History">Medical History</MenuItem>
                <MenuItem value="Family History">Family History</MenuItem>
                <MenuItem value="Drug History">Drug History</MenuItem>
                <MenuItem value="Allergies">Allergies</MenuItem>
                <MenuItem value="Addiction History">Addiction History</MenuItem>
                <MenuItem value="Surgeries">Surgeries</MenuItem>
                <MenuItem value="Menstrual History">Menstrual History</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Buttons Row */}
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Grid container justifyContent="space-between">
              <Grid
                item
                xs={12}
                md={12}
                lg={8}
                display="flex"
                justifyContent="flex-start"
                gap={1}
              >
                <Grid item xs={6} md={6} lg={5}>
                  <StyledButton
                    bgColor="#0E3F66"
                    onClick={handleStartRecording}
                    disabled={isRecording}
                    variant="contained"
                    sx={{
                      fontSize: { xs: "0.6rem", sm: "0.8rem" },
                      width: "90%",
                      height: "90%",
                      "&:hover": { backgroundColor: "#07367dff" },
                    }}
                  >
                    Start Recording
                  </StyledButton>
                </Grid>

                {/* Pause/Resume Button */}
                <Grid item xs={6} md={6} lg={4}>
                  <StyledButton
                    bgColor={isPaused ? "#5DAA60" : "#FFA500"}
                    onClick={
                      isPaused ? handleResumeRecording : handlePauseRecording
                    }
                    disabled={!isRecording}
                    variant="contained"
                    fullWidth
                    sx={{
                      fontSize: { xs: "0.6rem", sm: "0.8rem" },
                      width: "90%",
                      height: "90%",
                      "&:hover": {
                        backgroundColor: isPaused ? "#1b5e20" : "#FF8C00",
                      },
                    }}
                  >
                    {isPaused ? "Resume" : "Pause"}
                  </StyledButton>
                </Grid>

                <Grid item xs={6} md={6} lg={5}>
                  <StyledButton
                    bgColor="#FF3B30"
                    onClick={handleStopRecording}
                    disabled={!isRecording}
                    variant="contained"
                    sx={{
                      fontSize: { xs: "0.6rem", sm: "0.8rem" },
                      width: "90%",
                      height: "90%",
                      "&:hover": { backgroundColor: "#b71c1c" },
                    }}
                  >
                    Stop Recording
                  </StyledButton>
                </Grid>
              </Grid>

              <Grid item xs={6} md={6} lg={3}>
                <StyledButton
                  bgColor="#5DAA60"
                  onClick={() => {
                    if (mode === "add") {
                      handleSubmitRecording();
                    } else {
                      handleSubmitRecordingEdit({ edit: "group" });
                    }
                  }}
                  disabled={
                    isRecording ||
                    (mode === "edit" && sectionToEdit.length === 0)
                  }
                  variant="contained"
                  sx={{
                    fontSize: { xs: "0.6rem", sm: "0.8rem" },
                    width: "90%",
                    height: "90%",
                    "&:hover": { backgroundColor: "#1b5e20" },
                  }}
                >
                  Submit
                </StyledButton>
              </Grid>
            </Grid>
          </Box>

          {/* Waveform + Timer in same line */}
          {isRecording && streamRef.current && (
            <Box sx={{ mt: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  // keep responsive: waveform takes remaining space, timer fixed width
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <VoiceWaveform
                    stream={streamRef.current}
                    isRecording={isRecording}
                    isPaused={isPaused}
                  />
                </Box>

                <Box
                  sx={{
                    width: 110, // fixed space for the timer
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {/* Render timer only while recording — it will pause/resume via isPaused */}
                  <CountdownCircleTimer
                    key={timerKey}
                    isPlaying={isRecording && !isPaused}
                    duration={240} // 4:00 -> 240 seconds
                    size={90} // fixed size to maintain appearance
                    strokeWidth={8}
                    // colors + colorsTime to roughly match the attached image gradient (green -> teal -> blue)
                    colors={["#5DAA60", "#55C1A1", "#00A3E0", "#0E3F66"]}
                    colorsTime={[240, 160, 80, 0]}
                    onComplete={() => {
                      return { shouldRepeat: false, delay: 0 };
                    }}
                  >
                    {({ remainingTime }) => (
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.9rem", fontWeight: 700 }}
                      >
                        {formatMMSS(remainingTime)}
                      </Typography>
                    )}
                  </CountdownCircleTimer>
                </Box>
              </Box>
            </Box>
          )}

          {limitMessage && (
            <Typography
              variant="body2"
              color="error"
              sx={{ mt: 1, textAlign: "center" }}
            >
              {limitMessage}
            </Typography>
          )}

          {/* Audio Preview */}
          {audioBlob && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" gutterBottom>
                Preview:
              </Typography>
              <audio controls src={URL.createObjectURL(audioBlob)} />
            </Box>
          )}
        </DialogContent>
      )}

      <DialogActions />
    </Dialog>
  );
};

export default VoiceInputModal;
